import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { getTablePDFStyle } from '../../constants';
import { PDF_COLORS, PDF_FONT_FAMILY, PDF_FONT_SIZE } from '../shared/constants';
import TablePDF from '../shared/TablePDF';

/**
 * PDF Component for Hash Result Template
 * Replicates the visual design of HashResultTemplate component
 */
const HashResultTemplatePDF = ({
  data = null,
  title,
  headerText,
  dateSentEmail,
  groups = [],
  description,
  additionalInfo,
  dailyReport = null,
  orientation = 'portrait',
}) => {
  const baseStyles = getTablePDFStyle();

  const isEmail = value => {
    return typeof value === 'string' && value.includes('@');
  };

  const isHashField = fieldName => {
    return fieldName && (fieldName.toLowerCase() === 'hash' || fieldName.toLowerCase() === 'hash:');
  };

  const styles = StyleSheet.create({
    ...baseStyles,
    contentContainer: {
      marginBottom: 16,
    },
    date: {
      color: PDF_COLORS.TEXT_MUTED,
      fontSize: PDF_FONT_SIZE.VALUE,
      textAlign: 'center',
      marginBottom: 8,
    },
    fieldsContainer: { marginTop: 8 },
    fieldLabel: {
      color: PDF_COLORS.TEXT_SECONDARY,
      fontWeight: 'bold',
      width: '34%',
      fontSize: PDF_FONT_SIZE.TITLE,
      fontFamily: PDF_FONT_FAMILY,
    },
    fieldValue: {
      color: PDF_COLORS.TEXT,
      fontSize: PDF_FONT_SIZE.TITLE,
      textAlign: 'right',
      fontFamily: PDF_FONT_FAMILY,
      width: '66%',
    },
    fieldValueEmail: {
      color: PDF_COLORS.LINK,
      fontSize: PDF_FONT_SIZE.TITLE,
      textAlign: 'right',
      fontFamily: PDF_FONT_FAMILY,
      width: '66%',
    },
    fieldRowHash: {
      flexDirection: 'column',
      marginBottom: 6,
      alignItems: 'flex-start',
      borderBottomWidth: 0.2,
      borderBottomColor: PDF_COLORS.BORDER,
      paddingBottom: 4,
      minHeight: 20,
    },
    fieldValueHash: {
      color: PDF_COLORS.TEXT,
      fontSize: PDF_FONT_SIZE.TITLE,
      textAlign: 'left',
      fontFamily: PDF_FONT_FAMILY,
      width: '100%',
      marginTop: 3,
    },
    sectionTitle: {
      fontSize: PDF_FONT_SIZE.TITLE,
      fontWeight: 'bold',
      marginBottom: 8,
      color: PDF_COLORS.TEXT,
      padding: 6,
      backgroundColor: PDF_COLORS.HEADER_BG,
      borderBottomWidth: 0.2,
      borderBottomColor: PDF_COLORS.BORDER,
    },
    description: {
      color: PDF_COLORS.TEXT_SECONDARY,
      fontSize: PDF_FONT_SIZE.TITLE,
      lineHeight: 1.6,
      marginBottom: 8,
      textAlign: 'justify',
      fontFamily: PDF_FONT_FAMILY,
    },
    detailSection: { marginBottom: 16 },
    sectionsContainer: { marginTop: 8 },
    section: { marginBottom: 8 },
    fieldRow: {
      flexDirection: 'row',
      marginTop: 3,
      marginBottom: 3,
      minHeight: 16,
      alignItems: 'flex-start',
      borderBottomWidth: 0.2,
      borderBottomColor: PDF_COLORS.BORDER,
    },
    tableSection: {
      marginBottom: 16,
    },
  });

  const resolvedDailyReport =
    dailyReport || data?.dailyReport || data?.templateData?.templateData?.dailyReport || null;

  const dailyReportHeaders = resolvedDailyReport?.columns || [];
  const dailyReportData =
    resolvedDailyReport?.rows?.map((row, rowIndex) => {
      const record = { id: `daily-report-row-${rowIndex}` };

      dailyReportHeaders.forEach((header, cellIndex) => {
        record[header.dataIndex] = row.cells[cellIndex]?.value ?? '';
      });

      return record;
    }) || [];

  return (
    <Document>
      <Page size="A4" orientation={orientation} style={styles.page}>
        <View height={410}>
          <View>
            <Text style={styles.title}>{headerText || title}</Text>
          </View>
          {dateSentEmail && <Text style={styles.date}>{dateSentEmail}</Text>}

          <View style={styles.contentContainer}>
            {groups?.length > 0 && (
              <View style={styles.sectionsContainer}>
                {groups.map((group, groupIndex) => {
                  if (!group.fields?.length) return null;

                  return (
                    <View key={groupIndex} wrap={false} style={[styles.cardReport, styles.section]}>
                      <Text style={styles.sectionTitle}>{group.groupName}</Text>
                      <View style={styles.fieldsContainer}>
                        {group.fields.map((field, fieldIndex) => {
                          const isLastField = fieldIndex === group.fields.length - 1;
                          const isEmailValue = isEmail(field.fieldValue);
                          const isHash = isHashField(field.fieldName);

                          if (isHash) {
                            return (
                              <View
                                key={fieldIndex}
                                style={[
                                  styles.fieldRowHash,
                                  isLastField && { borderBottomWidth: 0, marginBottom: 0 },
                                ]}
                              >
                                <Text style={styles.fieldLabel}>{field.fieldName}</Text>
                                <Text style={styles.fieldValueHash}>{field.fieldValue}</Text>
                              </View>
                            );
                          }

                          return (
                            <View
                              key={fieldIndex}
                              style={[
                                styles.fieldRow,
                                isLastField && { borderBottomWidth: 0, marginBottom: 0 },
                              ]}
                            >
                              <Text style={styles.fieldLabel}>{field.fieldName}</Text>
                              <Text style={isEmailValue ? styles.fieldValueEmail : styles.fieldValue}>
                                {field.fieldValue}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    </View>
                  );
                })}
              </View>
            )}

            {dailyReportHeaders.length > 0 && dailyReportData.length > 0 && (
              <View wrap={false} style={[styles.cardReport, styles.tableSection]}>
                <TablePDF data={dailyReportData} headers={dailyReportHeaders} />
              </View>
            )}

            {(description || additionalInfo) && (
              <View wrap={false} style={[styles.cardReport, styles.detailSection]}>
                <Text style={styles.sectionTitle}>Asunto</Text>
                {description && <Text style={styles.description}>{description}</Text>}
                {additionalInfo && <Text style={styles.description}>{additionalInfo}</Text>}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default HashResultTemplatePDF;
